import {MapMarker, Paper, useMapRef} from '@yandex-delivery/blocks'
import {get} from '@yandex-delivery/utils'
import clsx from 'clsx'
import React, {useCallback, useEffect, useState} from 'react'

import {TitleSlot} from 'src/shared/components/desktop'

import {usePointTitle} from '../../hooks/use-point-title'
import {PointIcon} from '../../point-icon/PointIcon'
import {MAX_ZOOM_TO_SHOW_POPUP} from '../consts'
import {pointsToBounds} from '../utils'
import {PointClusterPlacemark} from './point-cluster-placemark/PointClusterPlacemark'
import {classes} from './PointPlacemark.classes'
import type {Props} from './types'

export function PointPlacemark(props: Props) {
  const {features, id, length, lnglat, onPointIdSelect, points, pointType, selectedPoint, setLocation, type} = props
  const map = useMapRef()

  const [isPointClusterPlacemarkVisible, setIsPointClusterPlacemarkVisible] = useState<boolean>(true)
  const point = points[id]
  const pointTitle = usePointTitle(point, pointType)

  const isSelected = id === selectedPoint?.id

  const setSelectedPointHandler = useCallback(() => {
    const latitude = get('position.latitude', point)
    const longitude = get('position.longitude', point)
    if (latitude && longitude) {
      setLocation({center: [longitude, latitude], duration: 500, zoom: 18})
    }
    onPointIdSelect(id)
  }, [id, onPointIdSelect, point, setLocation])

  const pointClusterPlacemarkClickHandler = useCallback(() => {
    if (features && features.length >= 2) {
      const lngLats = features.map(feature => feature.geometry.coordinates)
      const bounds = pointsToBounds(lngLats)
      if (bounds) {
        setLocation({bounds, duration: 500})
      }
    } else {
      setLocation({center: lnglat, zoom: MAX_ZOOM_TO_SHOW_POPUP, duration: 500})
    }
  }, [features, lnglat, setLocation])

  useEffect(() => {
    requestAnimationFrame(() => {
      if (map?.zoom === undefined) {
        setIsPointClusterPlacemarkVisible(true)
      } else {
        setIsPointClusterPlacemarkVisible(type === 'cluster' || map.zoom < MAX_ZOOM_TO_SHOW_POPUP)
      }
    })
  }, [map, map?.zoom, type])

  return isPointClusterPlacemarkVisible ? (
    <PointClusterPlacemark lnglat={lnglat} onClick={pointClusterPlacemarkClickHandler} pointsCount={length}>
      <PointIcon point={point} />
    </PointClusterPlacemark>
  ) : (
    <MapMarker coordinates={lnglat} onClick={setSelectedPointHandler}>
      <Paper className={clsx(classes.pvzMarkContainer, {[classes.pvzMarkSelected]: isSelected})}>
        <TitleSlot
          startIcon={<PointIcon className={clsx({[classes.invert]: isSelected})} point={point} />}
          title={pointTitle}
          titleTypography={{
            variant: 'caption1',
            thickness: 'medium',
            group: 'normal',
            color: isSelected ? 'invert' : 'main',
          }}
        />
      </Paper>
    </MapMarker>
  )
}
